import { wideGamutColor } from './utils/wideGamutColor'

/**
 * Object containing all CSS variable names for the Gem primitive colors.
 * @private These variables are used as a basis for ThemedColors and should
 * not be used in isolation without good reason.
 * @see https://www.figma.com/design/fXJgb6WJoQ6L1hpdCVpEh6/Colors?node-id=379-194&t=lvQTbg58t1bAINoe-4
 */
export const ColorPrimitives = {
  // Blues
  B050: '--gem-color-B050',
  B100: '--gem-color-B100',
  B150: '--gem-color-B150',
  B200: '--gem-color-B200',
  B250: '--gem-color-B250',
  B300: '--gem-color-B300',
  B350: '--gem-color-B350',
  B400: '--gem-color-B400',
  B450: '--gem-color-B450',
  B500: '--gem-color-B500',
  B550: '--gem-color-B550',
  B600: '--gem-color-B600',
  B650: '--gem-color-B650',
  B700: '--gem-color-B700',
  B800: '--gem-color-B800',
  B900: '--gem-color-B900',
  B999: '--gem-color-B999',

  // Neutrals
  N000: '--gem-color-N000',
  N050: '--gem-color-N050',
  N100: '--gem-color-N100',
  N150: '--gem-color-N150',
  N200: '--gem-color-N200',
  N250: '--gem-color-N250',
  N300: '--gem-color-N300',
  N350: '--gem-color-N350',
  N400: '--gem-color-N400',
  N500: '--gem-color-N500',
  N600: '--gem-color-N600',
  N700: '--gem-color-N700',
  N800: '--gem-color-N800',
  N900: '--gem-color-N900',
  N999: '--gem-color-N999',

  // Reds
  R100: '--gem-color-R100',
  R200: '--gem-color-R200',
  R300: '--gem-color-R300',
  R400: '--gem-color-R400',
  R500: '--gem-color-R500',
  R600: '--gem-color-R600',

  // Greens
  G100: '--gem-color-G100',
  G200: '--gem-color-G200',
  G300: '--gem-color-G300',
  G400: '--gem-color-G400',
  G500: '--gem-color-G500',
  G600: '--gem-color-G600',

  // Ambers
  A100: '--gem-color-A100',
  A200: '--gem-color-A200',
  A300: '--gem-color-A300',
  A400: '--gem-color-A400',
  A500: '--gem-color-A500',
  A600: '--gem-color-A600',

  // Purples
  P050: '--gem-color-P050',
  P100: '--gem-color-P100',
  P200: '--gem-color-P200',
  P300: '--gem-color-P300',
  P400: '--gem-color-P400',
  P500: '--gem-color-P500',
  P600: '--gem-color-P600',
  P700: '--gem-color-P700',
  P800: '--gem-color-P800',

  // Indigos
  IN100: '--gem-color-IN100',
  IN200: '--gem-color-IN200',
  IN300: '--gem-color-IN300',
  IN400: '--gem-color-IN400',
  IN500: '--gem-color-IN500',
  IN600: '--gem-color-IN600',

  // Ceruleans
  C100: '--gem-color-C100',
  C200: '--gem-color-C200',
  C300: '--gem-color-C300',
  C400: '--gem-color-C400',
  C500: '--gem-color-C500',
  C600: '--gem-color-C600',

  // Yellows a moment wears before anyone has chosen it a colour or a picture.
  // Figma: Customizable/Moments.
  StickyLightYellow: '--gem-color-sticky-light-yellow',
  StickyDarkYellow: '--gem-color-sticky-dark-yellow',

  Replaceable: '--gem-color-replaceable',
} as const

/**
 * Mapping of CSS variable names to their actual color value
 * @private - This object is intended to be used to generate
 * global styles and should not be used ad-hoc for styling.
 * @see @opallabs/gem -> GlobalGemColors
 */
export const ColorPrimitiveValues = {
  // Blues
  [ColorPrimitives.B050]: wideGamutColor('#FAFCFF'),
  [ColorPrimitives.B100]: wideGamutColor('#F6F9FE'),
  [ColorPrimitives.B150]: wideGamutColor('#EEF4FE'),
  [ColorPrimitives.B200]: wideGamutColor('#DDE8FC'),
  [ColorPrimitives.B250]: wideGamutColor('#C4D6F5'),
  [ColorPrimitives.B300]: wideGamutColor('#6BA1FF'),
  [ColorPrimitives.B350]: wideGamutColor('#5592FF'),
  [ColorPrimitives.B400]: wideGamutColor('#3D83FF'),
  [ColorPrimitives.B450]: wideGamutColor('#2A70EA'),
  [ColorPrimitives.B500]: wideGamutColor('#2866D3'),
  [ColorPrimitives.B550]: wideGamutColor('#225ABD'),
  [ColorPrimitives.B600]: wideGamutColor('#234C94'),
  [ColorPrimitives.B650]: wideGamutColor('#213A66'),
  [ColorPrimitives.B700]: wideGamutColor('#142B4B'),
  [ColorPrimitives.B800]: wideGamutColor('#0F2038'),
  [ColorPrimitives.B900]: wideGamutColor('#08172F'),
  [ColorPrimitives.B999]: wideGamutColor('#010102'),

  // Neutrals
  [ColorPrimitives.N000]: wideGamutColor('#FFFFFF'),
  [ColorPrimitives.N050]: wideGamutColor('#F9FAFC'),
  [ColorPrimitives.N100]: wideGamutColor('#F4F5F7'),
  [ColorPrimitives.N150]: wideGamutColor('#E9EAEB'),
  [ColorPrimitives.N200]: wideGamutColor('#D9DADB'),
  [ColorPrimitives.N250]: wideGamutColor('#BCBDBE'),
  [ColorPrimitives.N300]: wideGamutColor('#8B8C8C'),
  [ColorPrimitives.N350]: wideGamutColor('#7A7A7A'),
  [ColorPrimitives.N400]: wideGamutColor('#5F6061'),
  [ColorPrimitives.N500]: wideGamutColor('#474849'),
  [ColorPrimitives.N600]: wideGamutColor('#3A3B3C'),
  [ColorPrimitives.N700]: wideGamutColor('#2D2F30'),
  [ColorPrimitives.N800]: wideGamutColor('#272829'),
  [ColorPrimitives.N900]: wideGamutColor('#222324'),
  [ColorPrimitives.N999]: wideGamutColor('#1A1A1A'),

  // Reds
  [ColorPrimitives.R100]: wideGamutColor('#FF7878'),
  [ColorPrimitives.R200]: wideGamutColor('#FF6363'),
  [ColorPrimitives.R300]: wideGamutColor('#FF4D4D'),
  [ColorPrimitives.R400]: wideGamutColor('#D64040'),
  [ColorPrimitives.R500]: wideGamutColor('#BC3838'),
  [ColorPrimitives.R600]: wideGamutColor('#A33131'),

  // Greens
  [ColorPrimitives.G100]: wideGamutColor('#57DE93'),
  [ColorPrimitives.G200]: wideGamutColor('#00BF56'),
  [ColorPrimitives.G300]: wideGamutColor('#00A148'),
  [ColorPrimitives.G400]: wideGamutColor('#00853B'),
  [ColorPrimitives.G500]: wideGamutColor('#00692E'),
  [ColorPrimitives.G600]: wideGamutColor('#005224'),

  // Ambers
  [ColorPrimitives.A100]: wideGamutColor('#FFD68C'),
  [ColorPrimitives.A200]: wideGamutColor('#FFB736'),
  [ColorPrimitives.A300]: wideGamutColor('#EB9906'),
  [ColorPrimitives.A400]: wideGamutColor('#D47400'),
  [ColorPrimitives.A500]: wideGamutColor('#B05800'),
  [ColorPrimitives.A600]: wideGamutColor('#9C4900'),

  // Purples
  [ColorPrimitives.P050]: wideGamutColor('#E3D5FD'),
  [ColorPrimitives.P100]: wideGamutColor('#CBB2FE'),
  [ColorPrimitives.P200]: wideGamutColor('#BB99FF'),
  [ColorPrimitives.P300]: wideGamutColor('#A273FF'),
  [ColorPrimitives.P400]: wideGamutColor('#8955F2'),
  [ColorPrimitives.P500]: wideGamutColor('#6D3DCC'),
  [ColorPrimitives.P600]: wideGamutColor('#522E99'),
  [ColorPrimitives.P700]: wideGamutColor('#361F66'),
  [ColorPrimitives.P800]: wideGamutColor('#271D3A'),

  // Indigos
  [ColorPrimitives.IN100]: wideGamutColor('#235ED1'),
  [ColorPrimitives.IN200]: wideGamutColor('#0043C7'),
  [ColorPrimitives.IN300]: wideGamutColor('#003AAD'),
  [ColorPrimitives.IN400]: wideGamutColor('#003191'),
  [ColorPrimitives.IN500]: wideGamutColor('#002C83'),
  [ColorPrimitives.IN600]: wideGamutColor('#002773'),

  // Ceruleans
  [ColorPrimitives.C100]: wideGamutColor('#80D9FF'),
  [ColorPrimitives.C200]: wideGamutColor('#50C7FA'),
  [ColorPrimitives.C300]: wideGamutColor('#19BAFF'),
  [ColorPrimitives.C400]: wideGamutColor('#129DE3'),
  [ColorPrimitives.C500]: wideGamutColor('#157CB0'),
  [ColorPrimitives.C600]: wideGamutColor('#0B638F'),

  // Yellows
  [ColorPrimitives.StickyLightYellow]: wideGamutColor('#FFFFE2'),
  [ColorPrimitives.StickyDarkYellow]: wideGamutColor('#EBE1B4'),

  [ColorPrimitives.Replaceable]: wideGamutColor('#FF3CA0'),
} as const

/**
 * Object containing all themed CSS variable names for the Gem primitive colors.
 * Refered to as "Tokens" in the design files.
 * @see https://www.figma.com/design/fXJgb6WJoQ6L1hpdCVpEh6/Colors?node-id=209-477&t=lvQTbg58t1bAINoe-4
 * @example
 *   const style = { color: `var(${ThemedColors.Primary1})` }
 */
export const ThemedColors = {
  GemPrimary0: '--gem-color-gem-primary-0',
  GemPrimary1: '--gem-color-gem-primary-1',
  GemPrimary2: '--gem-color-gem-primary-2',
  GemPrimary3: '--gem-color-gem-primary-3',
  GemPrimary4: '--gem-color-gem-primary-4',
  GemPrimary5: '--gem-color-gem-primary-5',
  GemPrimary6: '--gem-color-gem-primary-6',
  GemPrimary7: '--gem-color-gem-primary-7',
  GemPrimary8: '--gem-color-gem-primary-8',
  Primary1: '--gem-color-primary-1',
  Primary2: '--gem-color-primary-2',
  Primary3: '--gem-color-primary-3',
  Primary4: '--gem-color-primary-4',
  Primary5: '--gem-color-primary-5',
  Primary6: '--gem-color-primary-6',
  Primary7: '--gem-color-primary-7',
  Primary8: '--gem-color-primary-8',
  Surface1: '--gem-color-surface-1',
  Surface2: '--gem-color-surface-2',
  Surface3: '--gem-color-surface-3',
  Surface4: '--gem-color-surface-4',
  Surface5: '--gem-color-surface-5',
  Surface6: '--gem-color-surface-6',
  Error1: '--gem-color-error-1',
  Error2: '--gem-color-error-2',
  Error3: '--gem-color-error-3',
  Success1: '--gem-color-success-1',
  Warning1: '--gem-color-warning-1',
  Info1: '--gem-color-info-1',
  StrokeError1: '--gem-color-stroke-error-1',
  StrokeInfo1: '--gem-color-stroke-info-1',
  StrokeSuccess1: '--gem-color-stroke-success-1',
  StrokePrimary1: '--gem-color-stroke-primary-1',
  StrokePrimary2: '--gem-color-stroke-primary-2',
  StrokeSecondary1: '--gem-color-stroke-secondary-1',
  StrokeSecondary2: '--gem-color-stroke-secondary-2',
  StrokeSecondary3: '--gem-color-stroke-secondary-3',
  StrokeSecondary4: '--gem-color-stroke-secondary-4',
  TextPrimary: '--gem-color-text-primary',
  TextSecondary: '--gem-color-text-secondary',
  TextContrast: '--gem-color-text-contrast',
  TextLinks: '--gem-color-text-links',
  TextContrastLinks: '--gem-color-text-contrast-links',
  TextSuccess: '--gem-color-text-success',
  TextError: '--gem-color-text-error',
  TextWarning: '--gem-color-text-warning',
  IconPrimary: '--gem-color-icon-primary',
  IconSecondary: '--gem-color-icon-secondary',
  IconContrast: '--gem-color-icon-contrast',
  IconLinks: '--gem-color-icon-links',
  IconContrastLinks: '--gem-color-icon-contrast-links',
  IconError: '--gem-color-icon-error',
  IconSuccess: '--gem-color-icon-success',
  IconWarning: '--gem-color-icon-warning',
  IconPublished: '--gem-color-icon-published',
  IconReplaceable: '--gem-color-icon-replaceable',
} as const

/**
 * Mapping of CSS variable names to their actual color value
 * @private - This object is intended to be used to generate
 * global styles and should not be used ad-hoc for styling.
 * @see `@opallabs/gem -> globalColorStyles`
 */

export const LightThemedColorValues = {
  [ThemedColors.GemPrimary0]: ColorPrimitiveValues[ColorPrimitives.P050],
  [ThemedColors.GemPrimary1]: ColorPrimitiveValues[ColorPrimitives.P100],
  [ThemedColors.GemPrimary2]: ColorPrimitiveValues[ColorPrimitives.P200],
  [ThemedColors.GemPrimary3]: ColorPrimitiveValues[ColorPrimitives.P300],
  [ThemedColors.GemPrimary4]: ColorPrimitiveValues[ColorPrimitives.P400],
  [ThemedColors.GemPrimary5]: ColorPrimitiveValues[ColorPrimitives.P500],
  [ThemedColors.GemPrimary6]: ColorPrimitiveValues[ColorPrimitives.P600],
  [ThemedColors.GemPrimary7]: ColorPrimitiveValues[ColorPrimitives.P700],
  [ThemedColors.GemPrimary8]: ColorPrimitiveValues[ColorPrimitives.P800],
  [ThemedColors.Primary1]: ColorPrimitiveValues[ColorPrimitives.B050],
  [ThemedColors.Primary2]: ColorPrimitiveValues[ColorPrimitives.B100],
  [ThemedColors.Primary3]: ColorPrimitiveValues[ColorPrimitives.B150],
  [ThemedColors.Primary4]: ColorPrimitiveValues[ColorPrimitives.B200],
  [ThemedColors.Primary5]: ColorPrimitiveValues[ColorPrimitives.B250],
  [ThemedColors.Primary6]: ColorPrimitiveValues[ColorPrimitives.B450],
  [ThemedColors.Primary7]: ColorPrimitiveValues[ColorPrimitives.B500],
  [ThemedColors.Primary8]: ColorPrimitiveValues[ColorPrimitives.B550],
  [ThemedColors.Surface1]: ColorPrimitiveValues[ColorPrimitives.N000],
  [ThemedColors.Surface2]: ColorPrimitiveValues[ColorPrimitives.N050],
  [ThemedColors.Surface3]: ColorPrimitiveValues[ColorPrimitives.N150],
  [ThemedColors.Surface4]: ColorPrimitiveValues[ColorPrimitives.N200],
  [ThemedColors.Surface5]: ColorPrimitiveValues[ColorPrimitives.N250],
  [ThemedColors.Surface6]: ColorPrimitiveValues[ColorPrimitives.N300],
  [ThemedColors.Error1]: ColorPrimitiveValues[ColorPrimitives.R400],
  [ThemedColors.Error2]: ColorPrimitiveValues[ColorPrimitives.R500],
  [ThemedColors.Error3]: ColorPrimitiveValues[ColorPrimitives.R600],
  [ThemedColors.Success1]: ColorPrimitiveValues[ColorPrimitives.G200],
  [ThemedColors.Warning1]: ColorPrimitiveValues[ColorPrimitives.A300],
  [ThemedColors.Info1]: ColorPrimitiveValues[ColorPrimitives.P400],
  [ThemedColors.StrokeError1]: ColorPrimitiveValues[ColorPrimitives.R400],
  [ThemedColors.StrokeInfo1]: ColorPrimitiveValues[ColorPrimitives.P400],
  [ThemedColors.StrokeSuccess1]: ColorPrimitiveValues[ColorPrimitives.G400],
  [ThemedColors.StrokePrimary1]: ColorPrimitiveValues[ColorPrimitives.B250],
  [ThemedColors.StrokePrimary2]: ColorPrimitiveValues[ColorPrimitives.B450],
  [ThemedColors.StrokeSecondary1]: ColorPrimitiveValues[ColorPrimitives.N150],
  [ThemedColors.StrokeSecondary2]: ColorPrimitiveValues[ColorPrimitives.N200],
  [ThemedColors.StrokeSecondary3]: ColorPrimitiveValues[ColorPrimitives.N300],
  [ThemedColors.StrokeSecondary4]: ColorPrimitiveValues[ColorPrimitives.N400],
  [ThemedColors.TextPrimary]: ColorPrimitiveValues[ColorPrimitives.N999],
  [ThemedColors.TextSecondary]: ColorPrimitiveValues[ColorPrimitives.N400],
  [ThemedColors.TextContrast]: ColorPrimitiveValues[ColorPrimitives.N000],
  [ThemedColors.TextLinks]: ColorPrimitiveValues[ColorPrimitives.B500],
  [ThemedColors.TextContrastLinks]: ColorPrimitiveValues[ColorPrimitives.B350],
  [ThemedColors.TextSuccess]: ColorPrimitiveValues[ColorPrimitives.G400],
  [ThemedColors.TextError]: ColorPrimitiveValues[ColorPrimitives.R400],
  [ThemedColors.TextWarning]: ColorPrimitiveValues[ColorPrimitives.A500],
  [ThemedColors.IconPrimary]: ColorPrimitiveValues[ColorPrimitives.N999],
  [ThemedColors.IconSecondary]: ColorPrimitiveValues[ColorPrimitives.N350],
  [ThemedColors.IconContrast]: ColorPrimitiveValues[ColorPrimitives.N000],
  [ThemedColors.IconLinks]: ColorPrimitiveValues[ColorPrimitives.B500],
  [ThemedColors.IconContrastLinks]: ColorPrimitiveValues[ColorPrimitives.B350],
  [ThemedColors.IconError]: ColorPrimitiveValues[ColorPrimitives.R400],
  [ThemedColors.IconSuccess]: ColorPrimitiveValues[ColorPrimitives.G400],
  [ThemedColors.IconWarning]: ColorPrimitiveValues[ColorPrimitives.A400],
  [ThemedColors.IconPublished]: ColorPrimitiveValues[ColorPrimitives.C400],
  [ThemedColors.IconReplaceable]:
    ColorPrimitiveValues[ColorPrimitives.Replaceable],
} as const

/**
 * Mapping of CSS variable names to their actual color value
 * @private - This object is intended to be used to generate
 * global styles and should not be used ad-hoc for styling.
 * @see @opallabs/gem -> GlobalGemColors
 */
export const DarkThemeColorValues = {
  [ThemedColors.GemPrimary0]: ColorPrimitiveValues[ColorPrimitives.P800],
  [ThemedColors.GemPrimary1]: ColorPrimitiveValues[ColorPrimitives.P700],
  [ThemedColors.GemPrimary2]: ColorPrimitiveValues[ColorPrimitives.P600],
  [ThemedColors.GemPrimary3]: ColorPrimitiveValues[ColorPrimitives.P500],
  [ThemedColors.GemPrimary4]: ColorPrimitiveValues[ColorPrimitives.P400],
  [ThemedColors.GemPrimary5]: ColorPrimitiveValues[ColorPrimitives.P300],
  [ThemedColors.GemPrimary6]: ColorPrimitiveValues[ColorPrimitives.P200],
  [ThemedColors.GemPrimary7]: ColorPrimitiveValues[ColorPrimitives.P100],
  [ThemedColors.GemPrimary8]: ColorPrimitiveValues[ColorPrimitives.P050],
  [ThemedColors.Primary1]: ColorPrimitiveValues[ColorPrimitives.B900],
  [ThemedColors.Primary2]: ColorPrimitiveValues[ColorPrimitives.B800],
  [ThemedColors.Primary3]: ColorPrimitiveValues[ColorPrimitives.B700],
  [ThemedColors.Primary4]: ColorPrimitiveValues[ColorPrimitives.B650],
  [ThemedColors.Primary5]: ColorPrimitiveValues[ColorPrimitives.B600],
  [ThemedColors.Primary6]: ColorPrimitiveValues[ColorPrimitives.B400],
  [ThemedColors.Primary7]: ColorPrimitiveValues[ColorPrimitives.B350],
  [ThemedColors.Primary8]: ColorPrimitiveValues[ColorPrimitives.B300],
  [ThemedColors.Surface1]: ColorPrimitiveValues[ColorPrimitives.N900],
  [ThemedColors.Surface2]: ColorPrimitiveValues[ColorPrimitives.N700],
  [ThemedColors.Surface3]: ColorPrimitiveValues[ColorPrimitives.N600],
  [ThemedColors.Surface4]: ColorPrimitiveValues[ColorPrimitives.N500],
  [ThemedColors.Surface5]: ColorPrimitiveValues[ColorPrimitives.N400],
  [ThemedColors.Surface6]: ColorPrimitiveValues[ColorPrimitives.N350],
  [ThemedColors.Error1]: ColorPrimitiveValues[ColorPrimitives.R300],
  [ThemedColors.Error2]: ColorPrimitiveValues[ColorPrimitives.R200],
  [ThemedColors.Error3]: ColorPrimitiveValues[ColorPrimitives.R100],
  [ThemedColors.Success1]: ColorPrimitiveValues[ColorPrimitives.G200],
  [ThemedColors.Warning1]: ColorPrimitiveValues[ColorPrimitives.A300],
  [ThemedColors.Info1]: ColorPrimitiveValues[ColorPrimitives.P300],
  [ThemedColors.StrokeError1]: ColorPrimitiveValues[ColorPrimitives.R300],
  [ThemedColors.StrokeInfo1]: ColorPrimitiveValues[ColorPrimitives.P300],
  [ThemedColors.StrokeSuccess1]: ColorPrimitiveValues[ColorPrimitives.G300],
  [ThemedColors.StrokePrimary1]: ColorPrimitiveValues[ColorPrimitives.B600],
  [ThemedColors.StrokePrimary2]: ColorPrimitiveValues[ColorPrimitives.B400],
  [ThemedColors.StrokeSecondary1]: ColorPrimitiveValues[ColorPrimitives.N600],
  [ThemedColors.StrokeSecondary2]: ColorPrimitiveValues[ColorPrimitives.N500],
  [ThemedColors.StrokeSecondary3]: ColorPrimitiveValues[ColorPrimitives.N300],
  [ThemedColors.StrokeSecondary4]: ColorPrimitiveValues[ColorPrimitives.N000],
  [ThemedColors.TextPrimary]: ColorPrimitiveValues[ColorPrimitives.N000],
  [ThemedColors.TextSecondary]: ColorPrimitiveValues[ColorPrimitives.N250],
  [ThemedColors.TextContrast]: ColorPrimitiveValues[ColorPrimitives.N900],
  [ThemedColors.TextLinks]: ColorPrimitiveValues[ColorPrimitives.B350],
  [ThemedColors.TextContrastLinks]: ColorPrimitiveValues[ColorPrimitives.B500],
  [ThemedColors.TextSuccess]: ColorPrimitiveValues[ColorPrimitives.G300],
  [ThemedColors.TextError]: ColorPrimitiveValues[ColorPrimitives.R200],
  [ThemedColors.TextWarning]: ColorPrimitiveValues[ColorPrimitives.A300],
  [ThemedColors.IconPrimary]: ColorPrimitiveValues[ColorPrimitives.N000],
  [ThemedColors.IconSecondary]: ColorPrimitiveValues[ColorPrimitives.N200],
  [ThemedColors.IconContrast]: ColorPrimitiveValues[ColorPrimitives.N999],
  [ThemedColors.IconLinks]: ColorPrimitiveValues[ColorPrimitives.B400],
  [ThemedColors.IconContrastLinks]: ColorPrimitiveValues[ColorPrimitives.B500],
  [ThemedColors.IconError]: ColorPrimitiveValues[ColorPrimitives.R100],
  [ThemedColors.IconSuccess]: ColorPrimitiveValues[ColorPrimitives.G100],
  [ThemedColors.IconWarning]: ColorPrimitiveValues[ColorPrimitives.A200],
  [ThemedColors.IconPublished]: ColorPrimitiveValues[ColorPrimitives.C200],
  [ThemedColors.IconReplaceable]:
    ColorPrimitiveValues[ColorPrimitives.Replaceable],
} as const

/**
 * @note **Do not use this object if a CSS Variable is possible. **
 * @example
 * // Use this instead
 * const style = { color: `var(${ThemedColors.Primary1})` }
 *
 * Set of primitive colors as defined in the Gem design system.
 * @see https://www.figma.com/file/fXJgb6WJoQ6L1hpdCVpEh6/Colors?node-id=0%3A1
 */
export const GemColors = {
  // Blues
  B050: ColorPrimitiveValues[ColorPrimitives.B050],
  B100: ColorPrimitiveValues[ColorPrimitives.B100],
  B150: ColorPrimitiveValues[ColorPrimitives.B150],
  B200: ColorPrimitiveValues[ColorPrimitives.B200],
  B250: ColorPrimitiveValues[ColorPrimitives.B250],
  B300: ColorPrimitiveValues[ColorPrimitives.B300],
  B350: ColorPrimitiveValues[ColorPrimitives.B350],
  B400: ColorPrimitiveValues[ColorPrimitives.B400],
  B450: ColorPrimitiveValues[ColorPrimitives.B450],
  B500: ColorPrimitiveValues[ColorPrimitives.B500],
  B550: ColorPrimitiveValues[ColorPrimitives.B550],
  B600: ColorPrimitiveValues[ColorPrimitives.B600],
  B650: ColorPrimitiveValues[ColorPrimitives.B650],
  B700: ColorPrimitiveValues[ColorPrimitives.B700],
  B800: ColorPrimitiveValues[ColorPrimitives.B800],
  B900: ColorPrimitiveValues[ColorPrimitives.B900],
  B999: ColorPrimitiveValues[ColorPrimitives.B999],

  // Neutrals
  N000: ColorPrimitiveValues[ColorPrimitives.N000],
  N050: ColorPrimitiveValues[ColorPrimitives.N050],
  N100: ColorPrimitiveValues[ColorPrimitives.N100],
  N150: ColorPrimitiveValues[ColorPrimitives.N150],
  N200: ColorPrimitiveValues[ColorPrimitives.N200],
  N250: ColorPrimitiveValues[ColorPrimitives.N250],
  N300: ColorPrimitiveValues[ColorPrimitives.N300],
  N350: ColorPrimitiveValues[ColorPrimitives.N350],
  N400: ColorPrimitiveValues[ColorPrimitives.N400],
  N500: ColorPrimitiveValues[ColorPrimitives.N500],
  N600: ColorPrimitiveValues[ColorPrimitives.N600],
  N700: ColorPrimitiveValues[ColorPrimitives.N700],
  N800: ColorPrimitiveValues[ColorPrimitives.N800],
  N900: ColorPrimitiveValues[ColorPrimitives.N900],
  N999: ColorPrimitiveValues[ColorPrimitives.N999],

  // Reds
  R100: ColorPrimitiveValues[ColorPrimitives.R100],
  R200: ColorPrimitiveValues[ColorPrimitives.R200],
  R300: ColorPrimitiveValues[ColorPrimitives.R300],
  R400: ColorPrimitiveValues[ColorPrimitives.R400],
  R500: ColorPrimitiveValues[ColorPrimitives.R500],
  R600: ColorPrimitiveValues[ColorPrimitives.R600],

  // Greens
  G100: ColorPrimitiveValues[ColorPrimitives.G100],
  G200: ColorPrimitiveValues[ColorPrimitives.G200],
  G300: ColorPrimitiveValues[ColorPrimitives.G300],
  G400: ColorPrimitiveValues[ColorPrimitives.G400],
  G500: ColorPrimitiveValues[ColorPrimitives.G500],
  G600: ColorPrimitiveValues[ColorPrimitives.G600],

  // Ambers
  A100: ColorPrimitiveValues[ColorPrimitives.A100],
  A200: ColorPrimitiveValues[ColorPrimitives.A200],
  A300: ColorPrimitiveValues[ColorPrimitives.A300],
  A400: ColorPrimitiveValues[ColorPrimitives.A400],
  A500: ColorPrimitiveValues[ColorPrimitives.A500],
  A600: ColorPrimitiveValues[ColorPrimitives.A600],

  // Purples
  P050: ColorPrimitiveValues[ColorPrimitives.P050],
  P100: ColorPrimitiveValues[ColorPrimitives.P100],
  P200: ColorPrimitiveValues[ColorPrimitives.P200],
  P300: ColorPrimitiveValues[ColorPrimitives.P300],
  P400: ColorPrimitiveValues[ColorPrimitives.P400],
  P500: ColorPrimitiveValues[ColorPrimitives.P500],
  P600: ColorPrimitiveValues[ColorPrimitives.P600],
  P700: ColorPrimitiveValues[ColorPrimitives.P700],
  P800: ColorPrimitiveValues[ColorPrimitives.P800],

  // Indigos
  IN100: ColorPrimitiveValues[ColorPrimitives.IN100],
  IN200: ColorPrimitiveValues[ColorPrimitives.IN200],
  IN300: ColorPrimitiveValues[ColorPrimitives.IN300],
  IN400: ColorPrimitiveValues[ColorPrimitives.IN400],
  IN500: ColorPrimitiveValues[ColorPrimitives.IN500],
  IN600: ColorPrimitiveValues[ColorPrimitives.IN600],

  // Ceruleans
  C100: ColorPrimitiveValues[ColorPrimitives.C100],
  C200: ColorPrimitiveValues[ColorPrimitives.C200],
  C300: ColorPrimitiveValues[ColorPrimitives.C300],
  C400: ColorPrimitiveValues[ColorPrimitives.C400],
  C500: ColorPrimitiveValues[ColorPrimitives.C500],
  C600: ColorPrimitiveValues[ColorPrimitives.C600],

  // Yellows
  StickyLightYellow: ColorPrimitiveValues[ColorPrimitives.StickyLightYellow],
  StickyDarkYellow: ColorPrimitiveValues[ColorPrimitives.StickyDarkYellow],
} as const
